import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import React, { useEffect, useCallback, useContext, useReducer } from "react";
import { useAuth } from "hooks/Auth";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { modal } from "components/HeadlessModal";
import { apiGet, apiPost, apiPut, messageFromException } from "helpers/Request";
import {
    transform as contentTransformer,
    ContentObjectSchema,
    prepare as contentPreparer,
} from "schemas/Content";
import EditForm from "./EditForm";
import CachedBanner from "Screens/Article/CachedBanner";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { transform } from "lodash";
import { ContentContext } from "context/Content";
import { AppContext } from "context/App";
import { toast } from "react-hot-toast";
import DangerButton from "components/Buttons/DangerButton";

// ensures the content formValue shape matches the form schema
const ensureContentShape = (content, contentFlow) => {
    if (content.hasOwnProperty("revived_from_cache")) {
        return content;
    }
    const allProps = contentFlow.metaProperties["content"].concat(
        contentFlow.metaProperties["content-lower"]
    );

    return Object.assign({}, content, {
        meta: transform(
            allProps,
            (result, metaProp, key) => {
                const val = content.metaProperties.find(
                    (mp) => mp.field === metaProp.field
                );
                result[metaProp.field] = val ? val.value : "";
            },
            {}
        ),
        tags: content.tags.map((tag) => {
            return { label: tag.name, value: tag.uuid };
        }),
        workflow: {
            value: contentFlow.workflows[0].id,
            label: contentFlow.workflows[0].name,
        },
        type: "page",
    });
};

const showSaveErrorModal = (err) => {
    modal.error({
        title: "Unable to save page",
        children: messageFromException(err),
    });
};

const showLoadErrorModal = (history, err) => {
    modal.error({
        title: "Unable to load page",
        children: messageFromException(err),
        buttons: () => (
            <DangerButton
                onClick={() => {
                    history.push("/app/dashboard");
                    modal.dismiss();
                }}
            >
                Try again
            </DangerButton>
        ),
    });
};

const reducer = (oldState, newState) => {
    return Object.assign({}, oldState, newState);
};

const prepareForSave = (values, content, contentFlow) => {
    const allProps = contentFlow.metaProperties["content"].concat(
        contentFlow.metaProperties["content-lower"]
    );

    const tags = values.tags.map((tag) => tag.value);

    const meta = Object.keys(values.meta)
        .map((key, i) => {
            const type = allProps.find((mp) => mp.field === key);
            return {
                field: key,
                value: values.meta[key],
                type: type ? type.type : "NoopMetaProperty",
            };
        })
        .filter((mp) => mp.value !== "");

    const domAttachment =
        values.dominantAttachment && values.dominantAttachment.uuid
            ? values.dominantAttachment.uuid
            : null;

    return contentPreparer(
        Object.assign({}, content, values, {
            tags: tags,
            meta: meta,
            dominant_attachment: domAttachment,
            workflow_id: values.workflow.value,
            urls: values.custom_url
                ? [{ url: values.custom_url, is_default: 1 }]
                : undefined,
            layout_template:
                values.layout_template === "Default"
                    ? null
                    : values.layout_template,
        })
    );
};

const loadContent = async (uuid, pub, setState) => {
    return apiGet("/v4/content/" + uuid, pub, {
        include: "dominantAttachment,urls",
    })
        .then((response) => {
            return contentTransformer(response);
        })
        .then((item) => {
            setState({
                content: item,
                loading: false,
            });
        });
};

function PagePage(props) {
    const { uuid } = useParams();
    const location = useLocation();
    const auth = useAuth();
    const history = useHistory();

    const { setCurrentNav } = useContext(AppContext);

    const [state, setState] = useReducer(reducer, {
        content: ContentObjectSchema,
        formValues: ContentObjectSchema,
        loading: false,
        saving: false,
        isUserEditable: false,
        performingStateUpdate: false,
        requestReload: useCallback(() => {
            loadContent(uuid, auth.publication, setState).catch((err) => {
                showLoadErrorModal(history, err);
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [uuid, auth.publication]),
    });

    const contentFlow = useSelector((state) => state.contentFlow);

    const onSubmit = (values) => {
        if (
            state.content.lock &&
            state.content.lock.user.uuid !== auth.user.uuid
        ) {
            console.log("not locked by user");
            return;
        } else if (state.content.uuid && !state.content.lock) {
            console.log("no content lock");
            return;
        }

        const data = prepareForSave(values, state.content, contentFlow);

        setState({ saving: true });
        const toastId = toast.loading("Saving...");

        const apiAction = state.content.uuid ? apiPut : apiPost;

        return apiAction(
            "/v4/content/" +
                (state.content.uuid ? state.content.uuid : "") +
                "?include=dominantAttachment,urls",
            auth.publication,
            data
        )
            .then((resp) => {
                const item = contentTransformer(resp);
                toast.success("Saved!", { id: toastId });

                if (!state.content.uuid) {
                    // was a new item, so redirect
                    apiPost("/v4/lock-request", auth.publication, {
                        srn: item.srn,
                    })
                        .then((resp) => {
                            history.push("/app/page/" + item.uuid);
                        })
                        .catch((err) => {
                            toast.show("Unable to acquire lock");
                        });
                }

                setState({
                    content: item,
                    saving: false,
                    loading: false,
                });

                // return releaseLockOnSave;
            })
            .catch((err) => {
                setState({ saving: false });
                toast.dismiss(toastId);
                showSaveErrorModal(err);
            });
    };

    const onRequestNavigate = (url) => {
        history.push(url);
    };

    useEffect(() => {
        if (!uuid || uuid === "new") {
            setState({
                loading: false,
                content: ContentObjectSchema,
            });
            return;
        }
        loadContent(uuid, auth.publication, setState).catch((err) => {
            showLoadErrorModal(history, err);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, uuid, auth.publication]);

    useEffect(() => {
        // fixes a bug where the console notes a component updating another, unmounted
        // component. just delay the update until the next tick
        setTimeout(() => {
            setState({
                formValues: ensureContentShape(state.content, contentFlow),
            });
        });
    }, [state.content, contentFlow]);

    useEffect(() => {
        setCurrentNav("content");
    }, [setCurrentNav]);

    useEffect(() => {
        if (
            state.content.uuid &&
            state.content.lock &&
            state.content.lock.user.uuid === auth.user.uuid
        ) {
            setState({ isUserEditable: true });
        } else if (!state.content.uuid) {
            setState({ isUserEditable: true });
        } else {
            setState({ isUserEditable: false });
        }
    }, [state.content, auth.user]);

    // content and form values are provided via context provider
    return (
        <ContentContext.Provider
            value={{
                state,
                setState,
            }}
        >
            <MainBlock className="z-0">
                <CachedBanner />
                <EditForm
                    onSubmit={onSubmit}
                    onRequestNavigate={onRequestNavigate}
                />
            </MainBlock>
        </ContentContext.Provider>
    );
}

export default PagePage;
